import React, {useCallback, useEffect, useRef, useState} from "react";
import {useEventListener} from "../../../infrastructure/hooks/useEventListener";
import {KEY_CODES} from "../../../core/consts";
import {NEW_SEARCH_WIDGET} from "../new-search-widget.consts";
import {observer} from "mobx-react";

export const NewSearchWidgetPopularAndResultsKeyboardClickComposer = (Widget) => (observer(({controller}) => {
  const {state, device} = controller;
  const {isSmallView, isRtl} = device;
  const {isPopularAndResultsActiveNavigation, isPopularAndResultsHighlighted, popularAndResultsList} = state;

  const ref = useRef();

  useEffect(() => {
    if (isPopularAndResultsHighlighted) {
      setHighlightedItemIndex(0)
    } else if (!isPopularAndResultsActiveNavigation) {
      setHighlightedItemIndex(null)
    }
  }, [isPopularAndResultsActiveNavigation]);


  let defaultListIndex: Number = popularAndResultsList?.find(list => list?.hasEntities)?.highlightedIndex || 0;
  const [highlightedItemIndex, setHighlightedItemIndex] = useState(0);
  const [highLightedListIndex, setHighlightedListIndex] = useState(defaultListIndex);

  const onKeyboardClick = useCallback((event) => {
    if (!isSmallView) {
      const {keyCode} = event;
      const {down, up, left, right, tab, enter} = KEY_CODES;

      if (isPopularAndResultsActiveNavigation) {
        const entitiesLength: Number = popularAndResultsList[highLightedListIndex]?.entities?.length;
        const filteredLists = popularAndResultsList?.filter(l => l?.hasEntities)

        if (entitiesLength > 1 && [down, up].includes(keyCode)) {
          event.preventDefault()

          if (keyCode === down) {
            const nextItem: Number = (highlightedItemIndex + 1).mod(entitiesLength);
            setHighlightedItemIndex(nextItem);
          } else if (highlightedItemIndex !== 0 && keyCode === up) {
            const previewItem: Number = (highlightedItemIndex - 1).mod(entitiesLength);
            setHighlightedItemIndex(previewItem);
          }
        } else if (filteredLists.length > 1 && [left, right].includes(keyCode)) {
          event.preventDefault()
          const pressLeft: Boolean = isRtl ? keyCode === right : keyCode === left;
          const pressRight: Boolean = isRtl ? keyCode === left : keyCode === right;

          let direction: Number = pressLeft ? -1 : (pressRight ? 1 : 0);
          let nextListIndex: Number = getNextListIndex(popularAndResultsList, direction, highLightedListIndex);

          const currentList = getList(highLightedListIndex);
          const nextList = getList(nextListIndex);

          const currentOffset: Number = findOffset(currentList);
          const nextOffset: Number = findOffset(nextList);

          const nextListLength: Number = popularAndResultsList[nextListIndex].entities.length;

          const offsetIndex: Number = highlightedItemIndex - currentOffset + nextOffset;
          const newIndex: Number = nextListLength <= offsetIndex ? nextListLength - 1 : offsetIndex;
          setHighlightedItemIndex(newIndex);
          setHighlightedListIndex(nextListIndex);
        } else if ([tab].includes(keyCode)) {
          setHighlightedListIndex(highLightedListIndex);
          setHighlightedItemIndex(highlightedItemIndex);

        } else if ([enter].includes(keyCode)) {
          event.preventDefault()
        }
      } else if (down === keyCode) {
        resetIndexes()
      }
    }
  });

  useEventListener('keydown', onKeyboardClick);

  const resetIndexes = () => {
    setHighlightedItemIndex(0);
    setHighlightedListIndex(defaultListIndex);
  }

  const getList = (sectionIndex: number) => {
    const section = ref.current.children[sectionIndex];
    return section.lastChild.firstChild.children;
  };

  const findOffset = (list: HTMLCollection) =>
    Array.from(list).findIndex(item => item.children.length > 0);

  const getNextListIndex = (lists, direction, index) => {
    const getIndex = (idx) => idx.mod(lists.length);
    const findListWithEntities = (idx) => lists?.find(list => list.highlightedIndex === idx && list?.hasEntities);

    let nextList = findListWithEntities(getIndex(index + direction));
    return nextList ? nextList.highlightedIndex : findListWithEntities(getIndex(index + 2 * direction))?.highlightedIndex;
  };

  const onMouseEnter = (index, listId) => {
    const listIndex = popularAndResultsList.findIndex(list => list.id === listId);
    setHighlightedListIndex(listIndex)
    setHighlightedItemIndex(index);
  }

  const newProps = {
    controller,
    onMouseEnter,
    competitionHighlightedIndex: highLightedListIndex === NEW_SEARCH_WIDGET.HIGHLIGHTED_LIST_INDEX.competitions && highlightedItemIndex,
    competitorHighlightedIndex: highLightedListIndex === NEW_SEARCH_WIDGET.HIGHLIGHTED_LIST_INDEX.competitors && highlightedItemIndex,
    athleteHighlightedIndex: highLightedListIndex === NEW_SEARCH_WIDGET.HIGHLIGHTED_LIST_INDEX.athletes && highlightedItemIndex
  };

  return <Widget {...newProps} ref={ref}/>;
}));
